import {
    tipoImovelTextRegistry,
    tipoPagamentoTextRegistry,
    tipoComissaoTextRegistry,
    conjuge,
} from "../dynamicTextRegistry";
import { maskCpf, maskPhoneBr } from "../../utils/masks";
import {
    formatBrlCurrencyForDoc,
    formatDecimalBrForDoc,
} from "../../utils/brFormat";

const TIPO_PAGAMENTO_CURRENCY_KEYS = new Set([
    "VALOR_TOTAL",
    "VALOR_ENTRADA",
    "VALOR_ARRAS",
    "VALOR_PERMUTA",
    "VALOR_SALDO",
]);

const TIPO_PAGAMENTO_DECIMAL_KEYS = new Set([
    "PERCENTUAL_MULTA",
    "JUROS_MORA",
]);

const TIPO_PAGAMENTO_DATE_KEYS = new Set([
    "DATA_PAGAMENTO",
    "DATA_ENTRADA",
]);

function getTipoPagamentoField(fields, key) {
    if (!fields || typeof fields !== "object") return "";
    const upper = fields[key];
    const lower = fields[key.toLowerCase()];
    if (upper !== undefined && upper !== null && upper !== "") return upper;
    if (lower !== undefined && lower !== null && lower !== "") return lower;
    if (upper !== undefined && upper !== null) return upper;
    if (lower !== undefined && lower !== null) return lower;
    return "";
}

/** CPF for preview/PDF-style output: ###.###.###-## (form state stays digits-only). */
function formatCpfPreview(value) {
    return maskCpf(value ?? "");
}

/** Telefone BR na prévia; estado do formulário segue só com dígitos. */
function formatPhonePreview(value) {
    return maskPhoneBr(value ?? "");
}

/** Format date as DD/MM/YYYY (Brazilian) for preview. ISO `YYYY-MM-DD` is parsed as local date (no UTC shift). */
function formatDateBR(value) {
    if (!value) return "";
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = s.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString("pt-BR");
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return s;
    return parsed.toLocaleDateString("pt-BR");
}

function escapeHtml(value) {
    if (value == null) return "";
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function strongLabel(text) {
    return `<strong>${escapeHtml(text)}</strong>`;
}

function replacePlaceholdersInString(template, values) {
    if (!template) return "";
    return template.replace(/\{(.*?)\}/g, (_, key) => values[key] ?? "");
}

function replacePlaceholdersInStringEscaped(template, values) {
    if (!template) return "";
    return template.replace(/\{(.*?)\}/g, (_, key) =>
        escapeHtml(values[key] ?? "")
    );
}

/** Bold field labels inside the filled `casado` conjuge snippet. */
function boldConjugeTemplateLabels(html) {
    return html
        .replace(/CÔNJUGE:/g, `${strongLabel("CÔNJUGE:")}`)
        .replace(/\bCPF:/g, `${strongLabel("CPF:")}`)
        .replace(/\bRG:/g, `${strongLabel("RG:")}`)
        .replace(/NACIONALIDADE:/g, `${strongLabel("NACIONALIDADE:")}`)
        .replace(/PROFISSÃO:/g, `${strongLabel("PROFISSÃO:")}`)
        .replace(/\bEMAIL:/g, `${strongLabel("EMAIL:")}`);
}

function renderConjugeBlockHtml(person) {
    if (person?.ESTADO_CIVIL_PESSOA_FISICA !== "casado") return "";
    const tpl = conjuge.casado;
    if (!tpl) return "";
    const valuesForPreview = {
        ...person,
        CPF_CONJUGE: formatCpfPreview(person.CPF_CONJUGE),
    };
    const filled = replacePlaceholdersInStringEscaped(tpl, valuesForPreview).trim();
    if (!filled) return "";
    return boldConjugeTemplateLabels(filled);
}

/** One inline line: bold labels, values escaped, single spaces between parts. */
function buildPessoaFisicaBlockHtml(person) {
    const parts = [
        `${strongLabel("NOME:")} ${escapeHtml(person.NOME_PESSOA_FISICA)}`,
        `${strongLabel("CPF:")} ${escapeHtml(formatCpfPreview(person.CPF_PESSOA_FISICA))}`,
        `${strongLabel("RG:")} ${escapeHtml(person.RG_PESSOA_FISICA)}`,
        `${strongLabel("NACIONALIDADE:")} ${escapeHtml(
            person.NACIONALIDADE_PESSOA_FISICA
        )}`,
        `${strongLabel("PROFISSÃO:")} ${escapeHtml(person.PROFISSAO_PESSOA_FISICA)}`,
        `${strongLabel("ESTADO CIVIL:")} ${escapeHtml(
            person.ESTADO_CIVIL_PESSOA_FISICA
        )}`,
    ];
    const conj = renderConjugeBlockHtml(person);
    if (conj) parts.push(conj);
    parts.push(
        `${strongLabel("ENDEREÇO:")} ${escapeHtml(
            person.RUA_ENDERECO_PESSOA_FISICA
        )}, ${strongLabel("nº")} ${escapeHtml(
            person.N_ENDERECO_PESSOA_FISICA
        )}, ${strongLabel("BAIRRO")} ${escapeHtml(
            person.BAIRRO_ENDERECO_PESSOA_FISICA
        )}, ${escapeHtml(person.MUN_ENDERECO_PESSOA_FISICA)} ${strongLabel(
            "- CEP:"
        )} ${escapeHtml(person.CEP_PESSOA_FISICA)}`,
        `${strongLabel("EMAIL:")} ${escapeHtml(person.EMAIL_PESSOA_FISICA)}`,
        `${strongLabel("TELEFONE:")} ${escapeHtml(
            formatPhonePreview(person.TEL_PESSOA_FISICA)
        )}`
    );
    return parts.join(" ");
}

function signatureBlock(name, cpf) {
    return `________________________\n${name ?? ""}\n${formatCpfPreview(cpf)}`;
}

function signatureBlockConjugeHtml(name, cpf) {
    const nameEsc = escapeHtml(name ?? "");
    const cpfEsc = escapeHtml(formatCpfPreview(cpf));
    return `<div class="signature-conjuge-block space-y-1 mb-1"><div>________________________</div><div>${nameEsc}</div><div>${cpfEsc}</div></div>`;
}

export function compromissoCompraVendaResolver(formData, userConfig) {
    const safeUserConfig = userConfig || {};

    const data = {
        ...safeUserConfig,
        ...formData,
    };

    data.ENDERECO_IMOBILIARIA =
        formData.ENDERECO_IMOBILIARIA !== undefined &&
        formData.ENDERECO_IMOBILIARIA !== null
            ? formData.ENDERECO_IMOBILIARIA
            : safeUserConfig.endereco_imobiliaria || "";
    data.CELULAR_IMOBILIARIA = formatPhonePreview(
        formData.CELULAR_IMOBILIARIA !== undefined &&
            formData.CELULAR_IMOBILIARIA !== null
            ? formData.CELULAR_IMOBILIARIA
            : safeUserConfig.celular_imobiliaria || ""
    );
    data.EMAIL_IMOBILIARIA =
        formData.EMAIL_IMOBILIARIA !== undefined &&
        formData.EMAIL_IMOBILIARIA !== null
            ? formData.EMAIL_IMOBILIARIA
            : safeUserConfig.email_imobiliaria || "";
    data.CRECI_IMOBILIARIA =
        formData.CRECI_IMOBILIARIA !== undefined &&
        formData.CRECI_IMOBILIARIA !== null
            ? formData.CRECI_IMOBILIARIA
            : safeUserConfig.creci_imobiliaria || "";
    data.SITE_IMOBILIARIA =
        formData.SITE_IMOBILIARIA !== undefined &&
        formData.SITE_IMOBILIARIA !== null
            ? formData.SITE_IMOBILIARIA
            : safeUserConfig.site_imobiliaria || "";

    if (!data.CRECI_CORRETOR && formData.CRECI) {
        data.CRECI_CORRETOR = formData.CRECI;
    }

    data.IMOBILIARIA =
        formData.IMOBILIARIA !== undefined && formData.IMOBILIARIA !== null
            ? formData.IMOBILIARIA
            : safeUserConfig.imobiliaria ||
              safeUserConfig.nome_imobiliaria ||
              "";

    if (formData.vendedores?.length) {
        data.VENDEDORES = formData.vendedores
            .map((v) => buildPessoaFisicaBlockHtml(v))
            .join('<br /><br />');

        data.ASSINATURA_VENDEDOR = formData.vendedores
            .map((v) =>
                signatureBlock(v.NOME_PESSOA_FISICA, v.CPF_PESSOA_FISICA)
            )
            .join("\n\n");

        data.ASSINATURA_CONJUGE_VENDEDOR = formData.vendedores
            .filter((v) => v.ESTADO_CIVIL_PESSOA_FISICA === "casado")
            .map((v) => signatureBlockConjugeHtml(v.NOME_CONJUGE, v.CPF_CONJUGE))
            .join('<div class="h-4" aria-hidden="true"></div>');
    }

    if (formData.compradores?.length) {
        data.COMPRADORES = formData.compradores
            .map((c) => buildPessoaFisicaBlockHtml(c))
            .join('<br /><br />');

        data.ASSINATURA_COMPRADOR = formData.compradores
            .map((c) =>
                signatureBlock(c.NOME_PESSOA_FISICA, c.CPF_PESSOA_FISICA)
            )
            .join("\n\n");

        data.ASSINATURA_CONJUGE_COMPRADOR = formData.compradores
            .filter((c) => c.ESTADO_CIVIL_PESSOA_FISICA === "casado")
            .map((c) => signatureBlockConjugeHtml(c.NOME_CONJUGE, c.CPF_CONJUGE))
            .join('<div class="h-4" aria-hidden="true"></div>');
    }

    if (formData.testemunhas?.length) {
        data.ASSINATURA_TESTEMUNHA = formData.testemunhas
            .map((t) =>
                signatureBlock(t.NOME_TESTEMUNHA, t.CPF_TESTEMUNHA)
            )
            .join("\n\n");
    }

    if (formData.tipo_imovel?.type) {
        const templateText =
            tipoImovelTextRegistry[formData.tipo_imovel.type];

        data.TIPO_IMOVEL = templateText?.replace(
            /\{(.*?)\}/g,
            (_, key) => formData.tipo_imovel.fields?.[key] || ""
        );
    }

    if (formData.tipo_pagamento?.type) {
        const templateText =
            tipoPagamentoTextRegistry[formData.tipo_pagamento.type];
        const fields = formData.tipo_pagamento.fields || {};

        data.TIPO_PAGAMENTO = templateText?.replace(
            /\{(.*?)\}/g,
            (_, key) => {
                const raw = getTipoPagamentoField(fields, key);
                if (TIPO_PAGAMENTO_CURRENCY_KEYS.has(key)) {
                    return formatBrlCurrencyForDoc(raw);
                }
                if (TIPO_PAGAMENTO_DECIMAL_KEYS.has(key)) {
                    return formatDecimalBrForDoc(raw);
                }
                if (TIPO_PAGAMENTO_DATE_KEYS.has(key)) {
                    return formatDateBR(raw);
                }
                return raw ?? "";
            }
        );
    }

    if (data.DATA_ASSINATURA) {
        data.DATA_ASSINATURA = formatDateBR(data.DATA_ASSINATURA);
    }

    if (formData.tipo_comissao?.type) {
        const templateText =
            tipoComissaoTextRegistry[formData.tipo_comissao.type];

        data.TIPO_COMISSAO = templateText?.replace(
            /\{(.*?)\}/g,
            (_, key) =>
                formData.tipo_comissao.fields?.[key] ||
                safeUserConfig[key] ||
                ""
        );
    }

    return data;
}
